import { ORDER_LIFECYCLE_CODES, ORDER_STATUS_CODES, type OrderStatusCode } from './model';

/**
 * De filterpillen boven de werklijst. "Afgerond" vat afgerond en gefactureerd
 * samen: voor kantoor is dat één begrip, en het scheelt een pil.
 */
export interface OrderFilter {
    key: string;
    label: string;
    statuses: readonly OrderStatusCode[];
}

export const ORDER_FILTERS: readonly OrderFilter[] = [
    { key: 'alle', label: 'Alle', statuses: ORDER_STATUS_CODES },
    { key: 'nieuw', label: 'Nieuw', statuses: ['new'] },
    { key: 'bevestigd', label: 'Bevestigd', statuses: ['confirmed'] },
    { key: 'ingepland', label: 'Ingepland', statuses: ['scheduled'] },
    { key: 'in-opname', label: 'In opname', statuses: ['surveying'] },
    { key: 'concept', label: 'Concept rapport', statuses: ['draft_report'] },
    { key: 'afgerond', label: 'Afgerond', statuses: ['completed', 'invoiced'] },
    { key: 'geannuleerd', label: 'Geannuleerd', statuses: ['cancelled'] },
] as const;

/**
 * Filters zonder eigen pil: je komt er via een link. *3 keuringen wachten op een
 * afspraak* op de Planning opent Keuringen met dit filter. De API kent geen
 * filter "zonder afspraak", dus het zijn de twee statussen vóór het inplannen.
 * Zolang zo'n filter aan staat, toont de filterbalk hem als extra pil.
 */
export const ORDER_LINK_FILTERS: readonly OrderFilter[] = [
    { key: 'wacht-op-afspraak', label: 'Wacht op afspraak', statuses: ['new', 'confirmed'] },
] as const;

export function filterFor(key: string): OrderFilter {
    return (
        [...ORDER_FILTERS, ...ORDER_LINK_FILTERS].find((filter) => filter.key === key) ??
        (ORDER_FILTERS[0] as OrderFilter)
    );
}

/** Bestaat dit filter, als pil of als link? Een onbekende sleutel in het adres valt terug op Alle. */
export function isOrderFilterKey(key: string): boolean {
    return [...ORDER_FILTERS, ...ORDER_LINK_FILTERS].some((filter) => filter.key === key);
}

/** Filtert deze pil op een deel van de statussen, of op alles? */
export function isEveryStatus(statuses: readonly OrderStatusCode[]): boolean {
    return ORDER_STATUS_CODES.every((code) => statuses.includes(code));
}

/**
 * Sorteren. `status` volgt de levenscyclus (nieuw eerst, gefactureerd laatst,
 * geannuleerd achteraan) en niet het alfabet. De API sorteert `status`
 * alfabetisch, dus die volgorde stelt `orderApi.list` per status samen.
 */
export type OrderSort = 'opname' | 'aanvraag' | 'nummer' | 'status';

/** De labels staan in de keuzelijst; "Sorteer:" zet de filterbalk ervoor. */
export const ORDER_SORTS: ReadonlyArray<{ key: OrderSort; label: string; api: string | null }> = [
    { key: 'opname', label: 'Opnamedatum', api: '-scheduled_at' },
    { key: 'aanvraag', label: 'Aanvraagdatum', api: '-created_at' },
    { key: 'nummer', label: 'Nummer', api: '-number' },
    { key: 'status', label: 'Status', api: null },
];

export function apiSortFor(sort: OrderSort): string | null {
    return ORDER_SORTS.find((option) => option.key === sort)?.api ?? null;
}

/** De volgorde van "Sorteer: status". */
export const LIFECYCLE_SEQUENCE: readonly OrderStatusCode[] = [
    ...ORDER_LIFECYCLE_CODES,
    'cancelled',
];

/** Binnen één status: de nieuwste aanvraag eerst. */
export const WITHIN_STATUS_SORT = '-created_at';

export interface OrderQuery {
    /** Sleutel van een filterpil. */
    status?: string;
    search?: string;
    /** Code van de dienst (`nta8060`), of leeg voor alle diensten. */
    service?: string;
    assigneeUuid?: string;
    sort?: OrderSort;
    page?: number;
    perPage?: number;
}

export interface LifecycleSlice {
    status: OrderStatusCode;
    /** Positie binnen die status, 0-based. */
    offset: number;
    count: number;
}

/**
 * Welk deel van elke status op één pagina valt als de lijst op levenscyclus
 * gesorteerd is: pagina `page` beslaat de plaatsen
 * `[(page - 1) · perPage, page · perPage)` van de statussen achter elkaar.
 */
export function lifecycleSlices(
    statuses: readonly OrderStatusCode[],
    totals: Partial<Record<OrderStatusCode, number>>,
    page: number,
    perPage: number,
): LifecycleSlice[] {
    const start = (page - 1) * perPage;
    const end = start + perPage;
    const slices: LifecycleSlice[] = [];
    let cursor = 0;

    for (const status of LIFECYCLE_SEQUENCE.filter((code) => statuses.includes(code))) {
        const total = totals[status] ?? 0;
        const from = Math.max(start, cursor);
        const to = Math.min(end, cursor + total);

        if (to > from) {
            slices.push({ status, offset: from - cursor, count: to - from });
        }

        cursor += total;
    }

    return slices;
}
